import type { Announcement, PlanId } from './types'

// Console-side announcement helpers: which published announcements a given
// recruiter should see, plus per-recruiter banner-dismissal persistence (mirrors
// lib/alert.ts). The admin sidecar is the source of truth for content; this just
// decides visibility in the browser.

export interface RecruiterContext {
  accountId?: string
  planId: PlanId
}

/** Is this published announcement live (not expired)? */
export function isLive(a: Announcement, now = Date.now()): boolean {
  if (a.status !== 'published') return false
  if (a.expiresAt && new Date(a.expiresAt).getTime() < now) return false
  return true
}

/** Does the announcement's audience include this recruiter? */
export function matchesAudience(a: Announcement, ctx: RecruiterContext): boolean {
  if (a.audience === 'plan') return !!a.audiencePlan && a.audiencePlan === ctx.planId
  if (a.audience === 'account') return !!a.audienceAccountId && a.audienceAccountId === ctx.accountId
  return true // 'all'
}

/** Announcements eligible to show this recruiter, on a given channel. */
export function eligible(
  list: Announcement[],
  ctx: RecruiterContext,
  channel: 'banner' | 'bell',
  now = Date.now(),
): Announcement[] {
  return list.filter(
    (a) => isLive(a, now) && a.channels.includes(channel) && matchesAudience(a, ctx),
  )
}

// ── banner dismissal persistence ──────────────────────────────────────────────
const KEY_PREFIX = 'vouchr.ann.dismissed.'
const keyFor = (recruiterId: string) => `${KEY_PREFIX}${recruiterId}`

export function loadDismissed(recruiterId: string | undefined | null): Set<string> {
  if (!recruiterId || typeof localStorage === 'undefined') return new Set()
  try {
    const raw = localStorage.getItem(keyFor(recruiterId))
    const arr = raw ? JSON.parse(raw) : []
    return new Set(Array.isArray(arr) ? (arr as string[]) : [])
  } catch {
    return new Set()
  }
}

export function saveDismissed(recruiterId: string | undefined | null, ids: Set<string>): void {
  if (!recruiterId || typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(keyFor(recruiterId), JSON.stringify([...ids]))
  } catch {
    /* quota / private mode — dismissal just won't persist this session */
  }
}
